<template>
  <v-row>
    <v-col class="py-2" cols="6">
      <v-card flat border class="overflow-scroll">
        <v-card-text class="py-5">
          <template v-if="loading || !workInProgressReport">
            <p class="my-1">
              <v-progress-circular
                indeterminate
                color="primary"
                :size="16"
                width="3"
                class="mr-2"
              ></v-progress-circular>
              <i>{{ t("workInProgressIsBeingLoaded") }}</i>
            </p>
          </template>
          <template v-else>
            <v-row>
              <v-col class="py-2" cols="6">
                <b>{{ t("workInProgressReportId") }}:</b>
              </v-col>
              <v-col class="py-2" cols="6">
                {{ workInProgressReport.id }}
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-2" cols="6">
                <b>{{ t("lastDateIncluded") }}:</b>
              </v-col>
              <v-col class="py-2" cols="6">
                {{ formatDate(workInProgressReport.last_date_included) }}
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-2" cols="6">
                <b>{{ t("status") }}:</b>
              </v-col>
              <v-col class="py-2" cols="6">
                {{ workInProgressReport.status }}
              </v-col>
            </v-row>
            <v-row>
              <v-col class="py-2" cols="6">
                <b>{{ t("generatingStartedAt") }}:</b>
              </v-col>
              <v-col class="py-2" cols="6">
                {{ workInProgressReport.started_at }}
              </v-col>
            </v-row>

            <template v-if="workInProgressReport.completed_at">
              <v-row>
                <v-col class="py-2" cols="6">
                  <b>{{ t("generatingCompletedAt") }}:</b>
                </v-col>
                <v-col class="py-2" cols="6">
                  {{ workInProgressReport.completed_at }}
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2" cols="6">
                  <b>{{ t("totalExclVat") }}:</b>
                </v-col>
                <v-col class="py-2" cols="6">
                  &euro;
                  {{ formatCurrency(workInProgressReport.total_excl_vat) }}
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2" cols="6"> </v-col>
                <v-col class="py-2" cols="6">
                  <span
                    @click="downloadExcelExportOfWorkInProgressReportAction"
                    class="link"
                  >
                    <b>{{ t("downloadExcelExport") }}</b>
                  </span>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-2" cols="6"> </v-col>
                <v-col class="py-2" cols="6">
                  <span
                    @click="downloadExcelExportOfWorkInProgressLineItemsReportAction"
                    class="link"
                  >
                    <b>{{ t("downloadExcelExportInclExactOnlineItems") }}</b>
                  </span>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              <template v-if="workInProgressReport.failed_at">
                <v-row>
                  <v-col class="py-2" cols="6">
                    <b>{{ t("generatingFailedAt") }}:</b>
                  </v-col>
                  <v-col class="py-2" cols="6">
                    {{ workInProgressReport.failed_at }}
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    {{ workInProgressReport.failure_message }}
                  </v-col>
                </v-row>
              </template>
            </template>
          </template>
        </v-card-text>
      </v-card>
    </v-col>
    <v-col class="py-2" cols="6">
      <div
        v-if="
          !loading && workInProgressReport && workInProgressReport.completed_at
        "
        class="table-legend-wrapper"
      >
        <table class="table-legend">
          <tbody>
          <tr>
            <td colspan="2">
              <b>
                {{ t("legendStatuses") }}
              </b>
            </td>
          </tr>
          <tr
            v-for="status in [
              'CHECKOUT_PERIOD_CONCEPT',
              'CHECKOUT_PERIOD_CREATED',
              'PRO_FORMA_CONCEPT',
              'PRO_FORMA_PENDING',
              'PRO_FORMA_APPROVED',
            ]"
          >
            <td>
              {{ t("workInProgressReportLineStatuses." + status + ".status") }}
            </td>
            <td>
              <i>
                {{
                  t(
                    "workInProgressReportLineStatuses." +
                      status +
                      ".description"
                  )
                }}
              </i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </v-col>
  </v-row>

  <v-card
    v-if="
      !loading && workInProgressReport && workInProgressReport.completed_at
    "
    flat class="mt-4">
    <v-card-title class="mb-2">
      <v-chip-group
        v-model="status"
        mandatory
        selected-class="bg-blue-darken-1"
        class="align-center"
        @update:model-value="getWorkInProgressReportLines">
        <h4 class="pr-5 equipmentItemFilterLabel">
          {{ t('status') }}:
        </h4>
        <v-chip
          :value="''"
          :disabled="linesLoading"
          flat>
          {{ t('all') }}
        </v-chip>
        <template v-for="status in statuses">
          <v-chip
            :value="status"
            :disabled="linesLoading"
            flat>
            {{ status }}
          </v-chip>
        </template>
      </v-chip-group>

      <v-chip-group>
        <h4 class="pr-5 equipmentItemFilterLabel equipmentItemFilterLabelInput">
            {{ t('job') }}:
        </h4>
        <v-row>
          <v-col cols="7">
            <v-autocomplete
              v-model="job"
              :items="jobs"
              item-value="id"
              item-title="title"
              :disabled="linesLoading"
              @update:model-value="getWorkInProgressReportLines"
              clearable
              :class="['filterInput']"
              density="compact"
              hide-details="true"
            />
          </v-col>
        </v-row>
      </v-chip-group>

      <v-chip-group>
        <h4 class="pr-5 equipmentItemFilterLabel equipmentItemFilterLabelInput">
            {{ t('client') }}:
        </h4>
        <v-row>
          <v-col cols="7">
            <v-autocomplete
              v-model="client"
              :items="clients"
              item-value="id"
              item-title="title"
              :disabled="linesLoading"
              @update:model-value="getWorkInProgressReportLines"
              clearable
              :class="['filterInput']"
              density="compact"
              hide-details="true"
            />
          </v-col>
        </v-row>
      </v-chip-group>

      <v-chip-group>
        <h4 class="pr-5 equipmentItemFilterLabel equipmentItemFilterLabelInput">
            {{ t('branch') }}:
        </h4>
        <v-row>
          <v-col cols="7">
            <v-autocomplete
              v-model="branch"
              :items="branches"
              item-value="id"
              item-title="title"
              :disabled="linesLoading"
              @update:model-value="getWorkInProgressReportLines"
              clearable
              :class="['filterInput']"
              density="compact"
              hide-details="true"
            />
          </v-col>
        </v-row>
      </v-chip-group>
    </v-card-title>
  </v-card>

  <v-card
    v-if="workInProgressReport && workInProgressReport.completed_at"
    flat
    border
    class="overflow-scroll mt-4"
  >
    <v-data-table-server
      :headers="headers"
      :items="workInProgressReportLines"
      :items-length="workInProgressReportLines.length"
      :items-per-page-text="rowsPerPageText"
      :items-per-page-options="rowsPerPageItems"
      v-model:items-per-page="pagination.rowsPerPage"
      v-model:page="pagination.page"
      hide-default-footer
      @update:sort-by="sortEvent"
      :loading="linesLoading"
      class="work-in-progress-report-lines-table"
    >
      <template v-slot:headers="{ isSorted, getSortIcon, toggleSort }">
        <tr>
          <template v-for="header in headers" :key="header.key">
            <th
              class="custom_header_table custom-bg"
              :class="{ 'text-right': header.align === 'end' }"
              :style="{ 'text-align: right': header.align === 'end' }"
            >
              <span
                class="d-flex align-center"
                @click="header.sortable ? toggleSort(header) : ''"
              >
                <span :class="{ 'cursor-pointer': header.sortable }">{{
                  header.title
                }}</span>
                <template v-if="isSorted(header)">
                  <v-icon :icon="getSortIcon(header)" size="small"></v-icon>
                </template>
              </span>
            </th>
          </template>
        </tr>
      </template>
      <template v-slot:item="{ item }: { item: any }">
        <tr :key="`${item.id}-${lastLoaded}`">
          <td>{{ item.branch_name }}</td>
          <td>{{ item.client_name }}</td>
          <td>
            <a @click="gotoProjectTab('CostOverview', item.job_id, $event)" href="#" class="link">{{ item.job_name }}</a>
          </td>
          <td class="whitespace-pre-wrap min-width-200">{{ item.job_wip_notes ?? '&ndash;' }}</td>
          <td>{{ formatDate(item.checkout_period_start_date) }}</td>
          <td>{{ formatDate(item.checkout_period_end_date) }}</td>
          <td>
            {{
              t("workInProgressReportLineStatuses." + item.status + ".status")
            }}
          </td>
          <td>{{ formatDate(item.status_date) }}</td>
          <td>{{ item.days_in_status }}</td>
          <td class="text-right">
            &euro; {{ formatCurrency(item.total_excl_vat) }}
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import sayErrorResponse from "@/mixins/sayErrorResponse";
import {
  fetchWorkInProgressReport,
  fetchWorkInProgressReportLines,
  downloadExcelExportOfWorkInProgressReport,
  downloadExcelExportOfWorkInProgressLineItemsReport
} from "@/api/workInProgressReports";
import { datatableOptions } from "@/mixins/datatableOptions";
import useProjectTabSelector from '@/composable/useProjectTabSelector';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { projectTabSelector } = useProjectTabSelector()

const loading = ref(false);
const linesLoading = ref(false);

const workInProgressReport = ref<any>(null);
const workInProgressReportLines = ref<any[]>([]);
const lastLoaded = ref(Date.now());

const headers: any = computed(() => [
  { title: t("branch"), key: "branch", sortable: true },
  { title: t("client"), key: "client", sortable: true },
  { title: t("job"), key: "job", sortable: true },
  { title: t("jobWipNotes"), key: "job_wip_notes", sortable: true },
  { title: t("dateStart"), key: "checkout_period_start_date", sortable: true },
  { title: t("dateEnd"), key: "checkout_period_end_date", sortable: true },
  { title: t("status"), key: "status", sortable: true },
  { title: t("statusSince"), key: "status_date", sortable: true },
  { title: t("daysInStatus"), key: "days_in_status", sortable: true },
  { title: t("totalExclVat"), key: "total_excl_vat", sortable: true },
]);

const statuses = computed(() => {
  return [
    'AFREKENPERIODE - CONCEPT',
    'AFREKENPERIODE - AANGEMAAKT',
    'PRO FORMA - CONCEPT',
    'PRO FORMA - IN AFWACHTING',
    'PRO FORMA - GOEDGEKEURD',
  ]
})
const jobs = ref<any[]>([]);
const clients = ref<any[]>([]);
const branches = computed(() => {
  const vaLocations = store.getters['cache.vanAmerongenLocations']
  return vaLocations.map((location: any) => {
    return {
      id: location.branch_id,
      title: `${location.name} ${location.city}`
    }
  })
})

const status = ref('')
const job = ref('')
const client = ref('')
const branch = ref('')

onBeforeMount(async () => {
  // UI
  store.commit("app.setPageTitle", t("viewWorkInProgressReport"));
  store.commit("app.setBreadcrumbs", [
    {
      text: t("workInProgressReport", 2),
      routeName: "WorkInProgressReportsList",
    },
    { text: t("viewReport") },
  ]);
  store.dispatch('cache.vanAmerongenLocations.fetch')
});

const { rowsPerPageItems, rowsPerPageText, pagination } =
  datatableOptions(false);

const getWorkInProgressReport = async () => {
  try {
    const response = await fetchWorkInProgressReport(route.params.id);
    if (!response.data.data.status) {
      loading.value = false;
      sayErrorResponse(response);
    } else {
      loading.value = false;
      workInProgressReport.value = response.data.data;
      clients.value = response.data.clients.map((client: any) => {
        return { id: client.id, title: client.name };
      });
      jobs.value = response.data.jobs.map((job: any) => {
        return { id: job.id, title: job.job_name };
      });
    }
  } catch (error) {
    loading.value = false;
    sayErrorResponse(error);
  }
};

const getWorkInProgressReportLines = async () => {
  linesLoading.value = true;
  const params = {
    status: status.value,
    job: job.value,
    client: client.value,
    branch: branch.value,
    ...pagination.value
  };
  try {
    const response = await fetchWorkInProgressReportLines(route.params.id,
      params
    );
    workInProgressReportLines.value = [];

    // The setTimeout and lastLoaded are to prevent a warning for duplicate keys
    setTimeout(() => {
      lastLoaded.value = Date.now();
      workInProgressReportLines.value = response.data.data;
      linesLoading.value = false;
    }, 1);
  } catch (error) {
    linesLoading.value = false;
    sayErrorResponse(error);
  }
};

onMounted(async () => {
  getWorkInProgressReport();
  getWorkInProgressReportLines();
});

const sortEvent = (value: any) => {
  if (value.length === 0) {
    return;
  }

  pagination.value.sortBy = value[0].key;
  pagination.value.descending = value[0].order === "desc" ? true : false;

  getWorkInProgressReportLines();
};

const formatDate = (date: string) => {
  if (!date) {
    return "";
  }
  const [year, month, day] = date.split("-");
  return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
};

const formatCurrency = (value: number) =>
  Number(value).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const downloadStarted = ref(false);

const downloadExcelExportOfWorkInProgressReportAction = async () => {
  if (downloadStarted.value) {
    return;
  }
  downloadStarted.value = true;
  try {
    const response = await downloadExcelExportOfWorkInProgressReport(
      route.params.id
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      "work-in-progress-report-" +
        route.params.id +
        "-last-date-included-" +
        workInProgressReport.value.last_date_included +
        ".xlsx"
    );
    document.body.appendChild(link);
    link.click();
    downloadStarted.value = false;
  } catch (error) {
    sayErrorResponse(error);
    downloadStarted.value = false;
  }
};

const downloadExcelExportOfWorkInProgressLineItemsReportAction = async () => {
  if (downloadStarted.value) {
    return;
  }
  downloadStarted.value = true;
  try {
    const response = await downloadExcelExportOfWorkInProgressLineItemsReport(
      route.params.id
    );
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      "work-in-progress-report-line-items-" +
        route.params.id +
        "-last-date-included-" +
        workInProgressReport.value.last_date_included +
        ".xlsx"
    );
    document.body.appendChild(link);
    link.click();
    downloadStarted.value = false;
  } catch (error) {
    sayErrorResponse(error);
    downloadStarted.value = false;
  }
};

const gotoProjectTab = (tabName: string, jobId: any, e: any) => {
  // use router to navigate to the correct tab
  e.preventDefault();
  projectTabSelector.value = tabName
  router.push({ name: 'JobsView', params: { id: jobId }})
}
</script>

<style scoped>
.text-normal {
  font-weight: normal;
}

.bg-white {
  background-color: #fff;
}

.table-legend-wrapper {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.table-legend {
  width: 100%;
  border-collapse: collapse;
}
.table-legend td {
  padding: 0.75rem 1rem;
  font-size: 0.9em;
}
.table-legend tr:not(:first-child) td {
  font-size: 0.8em;
}
.table-legend tr:not(:last-child) td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

td {
  white-space: nowrap;
}

.link {
  cursor: pointer;
  color: rgb(30, 136, 229);
  text-decoration: underline;
  font-weight: 500;
}

.work-in-progress-report-lines-table tr td {
  transition: none !important;
}

.work-in-progress-report-lines-table.v-data-table--loading tr td {
  opacity: 0.5 !important;
}

.equipmentItemFilterLabel {
  padding-top: 10px;
  width: 120px;
}

.equipmentItemFilterLabelInput {
  padding-top: 15px;
}

.whitespace-pre-wrap {
  white-space: pre-wrap;
}

.min-width-200 {
  min-width: 200px;
}
</style>
